import { Injectable } from '@angular/core'
import { Platform } from '@ionic/angular'
import { BehaviorSubject } from 'rxjs'
import { MapListFilter } from '../maplist-filter/maplist-filter.fixtures'

enum SettingsOptionKey {
  isOnboardingCompleted = 'is-onboarding-completed',
  isNotifcationsEnabled = 'is-notifications-enabled-',
  pushNotificationToken = 'push-notification-token',
  showStoreDownload = 'show-store-download',
  mapListFilter = 'map-list-filter',
  interfaceMode = 'interface-mode',
  unitSystem = 'unit-system',
  showMapHint = 'show-map-hint',
  developerMode = 'developer-mode',
}

export enum NotificationType {
  chat = 'chat',
  job = 'job',
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  readonly isMobile: boolean
  readonly isAndroid: boolean
  readonly isIos: boolean

  isAnyNotificationsEnabled: BehaviorSubject<boolean> = new BehaviorSubject(
    this.getIsNotificationsEnabled(NotificationType.chat) ||
      this.getIsNotificationsEnabled(NotificationType.job)
  )

  constructor(platform: Platform) {
    this.isAndroid = platform.is('android')
    this.isIos = platform.is('ios')
    this.isMobile = this.isAndroid || this.isIos
  }

  getOnboardingCompleted(): boolean {
    return JSON.parse(
      localStorage.getItem(SettingsOptionKey.isOnboardingCompleted)
    )
  }

  setOnboardingCompleted(completed: boolean) {
    localStorage.setItem(
      SettingsOptionKey.isOnboardingCompleted,
      JSON.stringify(completed)
    )
  }

  getShowStoreDownload(): boolean {
    return (
      JSON.parse(localStorage.getItem(SettingsOptionKey.showStoreDownload)) ??
      true
    )
  }

  setShowStoreDownload(showStoreDownload: boolean) {
    localStorage.setItem(
      SettingsOptionKey.showStoreDownload,
      JSON.stringify(showStoreDownload)
    )
  }

  getUnitSystem(): string {
    return localStorage.getItem(SettingsOptionKey.unitSystem)
  }

  setUnitSystem(unit: string) {
    localStorage.setItem(SettingsOptionKey.unitSystem, unit)
  }

  getIsNotificationsEnabled(type: NotificationType): boolean {
    return JSON.parse(
      localStorage.getItem(SettingsOptionKey.isNotifcationsEnabled + type)
    )
  }

  setIsNotificationsEnabled(
    isEnabled: boolean,
    type: NotificationType = undefined
  ) {
    if (!type) {
      // turn off all notifications, if type is not specified
      this.setIsNotificationsEnabled(isEnabled, NotificationType.chat)
      this.setIsNotificationsEnabled(isEnabled, NotificationType.job)
      if (this.isAnyNotificationsEnabled.value !== false) {
        this.isAnyNotificationsEnabled.next(false)
      }
      return
    }

    localStorage.setItem(
      SettingsOptionKey.isNotifcationsEnabled + type,
      JSON.stringify(isEnabled)
    )

    const isAnyEnabled =
      this.getIsNotificationsEnabled(NotificationType.chat) ||
      this.getIsNotificationsEnabled(NotificationType.job)
    if (this.isAnyNotificationsEnabled.value !== isAnyEnabled) {
      this.isAnyNotificationsEnabled.next(isAnyEnabled)
    }
  }

  getPushNotificationToken(): string {
    return localStorage.getItem(SettingsOptionKey.pushNotificationToken)
  }

  setPushNotificationToken(token: string) {
    localStorage.setItem(SettingsOptionKey.pushNotificationToken, token)
  }

  setMapListFilter(filters: MapListFilter[]) {
    localStorage.setItem(
      SettingsOptionKey.mapListFilter,
      JSON.stringify(filters)
    )
  }

  getMapListFilters(): MapListFilter[] {
    const json: any[] = JSON.parse(
      localStorage.getItem(SettingsOptionKey.mapListFilter)
    )
    if (!json) {
      return undefined
    }
    return json.map((j) => MapListFilter.fromJson(j))
  }

  getInterfaceMode(): string {
    return localStorage.getItem(SettingsOptionKey.interfaceMode)
  }

  setInterfaceMode(mode: string) {
    localStorage.setItem(SettingsOptionKey.interfaceMode, mode)
  }

  getShowMapHint(): boolean {
    return (
      JSON.parse(localStorage.getItem(SettingsOptionKey.showMapHint)) ?? true
    )
  }

  setShowMapHint(show: boolean) {
    localStorage.setItem(SettingsOptionKey.showMapHint, JSON.stringify(show))
  }

  getDeveloperMode(): boolean {
    return (
      JSON.parse(localStorage.getItem(SettingsOptionKey.developerMode)) ?? false
    )
  }

  setDeveloperMode(useDeveloperMode: boolean) {
    localStorage.setItem(
      SettingsOptionKey.developerMode,
      JSON.stringify(useDeveloperMode)
    )
  }
}
